/**
 * Le bandeau d'expiration — RESTE-A-FAIRE lot F, point A.
 *
 * Fonctions PURES, sans React ni requête réseau : le calcul de « dans combien
 * de temps, et avec quelle intensité » se teste exhaustivement ici
 * (`echeance.test.ts`), indépendamment de son affichage dans les quatre coques
 * (`components/EtatTenant.tsx` porte le composant lui-même).
 *
 * ★ « DISCRET à J-30, INSISTANT à J-2 — une gradation, pas un interrupteur »
 * (consigne). `intensiteEcheance` rend un nombre CONTINU entre 0 et 1, pas un
 * booléen ni un jeu de trois seuils arbitraires : c'est ce nombre, pas le
 * nombre de jours brut, que `EtatTenant.tsx` doit lire pour choisir un style —
 * une même gradation vaut pour la couleur, l'épaisseur du texte et la bordure,
 * sans qu'un seuil ne « bascule » tout d'un coup à J-15 par exemple. Un
 * théâtre qui vit dans l'outil tous les jours ne doit jamais avoir l'impression
 * qu'un jour de plus a fait sauter l'écran d'un état à l'autre.
 */

/** Le bandeau apparaît à partir de ce nombre de jours restants — « discret ». */
export const SEUIL_BANDEAU_JOURS = 30

/**
 * En dessous de ce seuil, l'intensité plafonne à 1 (« insistant ») : rester
 * strictement monotone jusqu'à J-0 rendrait les deux derniers jours presque
 * indiscernables du reste — c'est l'inverse de l'effet recherché.
 */
export const SEUIL_INSISTANT_JOURS = 2

const JOUR_MS = 24 * 60 * 60 * 1000

/**
 * Jours entiers restants avant `expireLe`, arrondis vers le haut : à 23 h 59
 * d'un jour donné, il reste encore « 1 jour », pas 0 — la même convention
 * que `plateforme/ordonnanceur/taches.ts` (`joursRestants`, arrondi bas côté
 * serveur pour les jalons J-7/J-2/jour J) inverserait le sens : ici, on affiche
 * un compte à rebours à un humain, qui doit voir « 1 jour » tant qu'il en
 * reste un peu, jamais « 0 jour » avant l'heure.
 */
export function joursRestants(expireLe: string, maintenant: Date = new Date()): number {
  const cible = new Date(expireLe).getTime()
  return Math.ceil((cible - maintenant.getTime()) / JOUR_MS)
}

/**
 * Intensité de 0 (discret, J-30) à 1 (insistant, J-2 et en deçà) — `undefined`
 * hors de la fenêtre d'affichage : plus de 30 jours restants (rien à montrer
 * encore), ou déjà expiré (0 jour ou moins : c'est l'écran de blocage plein
 * écran, `EcranTenantBloquant`, qui prend le relais — jamais le bandeau et
 * l'écran de blocage à la fois).
 */
export function intensiteEcheance(jours: number): number | undefined {
  if (jours > SEUIL_BANDEAU_JOURS || jours <= 0) return undefined
  const etendue = SEUIL_BANDEAU_JOURS - SEUIL_INSISTANT_JOURS
  const brut = 1 - Math.max(0, jours - SEUIL_INSISTANT_JOURS) / etendue
  return Math.min(1, Math.max(0, brut))
}

/**
 * « Votre démonstration se termine dans X jours » — et ses bords : le jour
 * même et demain se disent, jamais « dans 0 jour » ni « dans 1 jours ».
 * `jours <= 0` n'est normalement jamais atteint par le bandeau lui-même
 * (`intensiteEcheance` rend `undefined` à ce point, cf. ci-dessus), mais cette
 * fonction reste correcte isolément — c'est ce que `echeance.test.ts` prouve.
 */
export function messageEcheance(jours: number): string {
  if (jours <= 0) return 'Votre démonstration se termine aujourd’hui.'
  if (jours === 1) return 'Votre démonstration se termine demain.'
  return `Votre démonstration se termine dans ${String(jours)} jours.`
}
